import {tokens} from '@commercial-helios/ui/theme'
import React from 'react'
import styled from 'styled-components'

import type {BlogPostCardFragment} from '@helios/blog/src/graphql-types'
import Image from '@helios/shared/components/image'
import LocalizedLink from '@helios/shared/components/localized-link'
import {useDateFormatter} from '@helios/shared/hooks/use-date-formatter'
import {Text} from '@helios/shared/styles/typography'
import {focusStyles} from '@helios/shared/styles/utils'
import type ImageSource from '@helios/shared/types/image'
import mq from '@helios/shared/utils/media-query'
import {truncate} from '@helios/shared/utils/truncate'

const BLOG_URL = 'https://blog.pleo.io'

type Category = {
    name: string
    slug: string
}

interface BlogPostCardWebsite {
    uid: string
    data: BlogPostCardFragment['data'] & {
        hero_image?: ImageSource
        publish_date?: string
    }
}

export type Props = {
    slug: string
    image?: ImageSource
    title: string
    category: Category
    excerpt: string
    publishDate: string
    className?: string
    hideImageOnMobile?: boolean
    isExternal?: boolean
}

const isOnBlog = (node: BlogPostCardFragment | BlogPostCardWebsite): node is BlogPostCardFragment =>
    node.data.publishDate !== undefined

export const nodeToPostProps = (node: BlogPostCardFragment | BlogPostCardWebsite): Props => {
    const props = {
        slug: node.uid,
        title: node.data.title,
        category: {
            name: node.data.category.document?.data.name,
            slug: node.data.category.uid,
        },
        excerpt: node.data.excerpt,
    }

    return isOnBlog(node)
        ? {
              ...props,
              image: node.data.heroImage,
              publishDate: node.data.publishDate || '',
          }
        : {
              ...props,
              image: node.data.hero_image,
              publishDate: node.data.publish_date || '',
          }
}

export const BlogPostCard = ({
    slug,
    image,
    title,
    category,
    excerpt,
    publishDate,
    className,
    hideImageOnMobile = false,
    isExternal = false,
}: Props) => {
    const formatDate = useDateFormatter()

    return (
        <ImageContentWrapper className={className}>
            {image?.url && (
                <Link href={slug} isExternal={isExternal}>
                    <StyledImage
                        $hideOnMobile={hideImageOnMobile}
                        image={image}
                        width={373}
                        data-blog-post-card-image
                    />
                </Link>
            )}

            <ContentWrapper>
                <Details>
                    <Text
                        as={Link}
                        variant="label-base"
                        color="brand"
                        href={`category/${category.slug}/`}
                        isExternal={isExternal}
                    >
                        {category.name}
                    </Text>

                    <DetailsSeparator />

                    <Text as="time" dateTime={publishDate} variant="label-base" color="lightMuted">
                        {formatDate(publishDate || '')}
                    </Text>
                </Details>

                <Text as="h2">
                    <Link href={slug} isExternal={isExternal}>
                        {title}
                    </Link>
                </Text>

                <Text variant="paragraph-base" color="lightMuted">
                    {truncate(excerpt, 140)}
                </Text>
            </ContentWrapper>

            {hideImageOnMobile && <HR />}
        </ImageContentWrapper>
    )
}

interface LinkProps {
    href: string
    children: React.ReactNode
    isExternal: boolean
}

const Link = ({href, isExternal, children, ...props}: LinkProps) =>
    isExternal ? (
        <a href={`${BLOG_URL}/en/${href}`} {...props}>
            {children}
        </a>
    ) : (
        <LocalizedLink to={`/${href}`} {...props}>
            {children}
        </LocalizedLink>
    )

const mobileBreakpoint = 'xs'

const ImageContentWrapper = styled.div`
    display: grid;
    row-gap: ${tokens.spacing20};
    width: 100%;
    align-content: start;
    padding-top: ${tokens.spacing2};

    a {
        display: block;
    }

    a:focus {
        ${focusStyles()}
        border-radius: ${tokens.arc4};
    }

    a:focus img {
        border-radius: ${tokens.arc4};
    }
`

const ContentWrapper = styled.div`
    display: grid;
    row-gap: ${tokens.spacing10};
`

const Details = styled.div`
    display: grid;
    grid-auto-flow: column;
    justify-content: start;
    align-items: center;
    column-gap: ${tokens.spacing10};
`

const DetailsSeparator = styled.div`
    width: 4px;
    height: 4px;
    background: ${tokens.shade600};
    border-radius: ${tokens.circle};
`

const StyledImage = styled(Image)<{$hideOnMobile: boolean}>`
    display: ${(props) => (props.$hideOnMobile ? 'none' : 'block')};
    width: 100%;
    height: 200px;
    object-fit: cover;

    ${mq.min(mobileBreakpoint)} {
        display: block;
    }
`

const HR = styled.hr`
    ${mq.min(mobileBreakpoint)} {
        display: none;
    }
`
